import {
  Surface,
  SurfaceContent,
  SurfaceHeader,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Skeleton } from "@-/dev.improve.haniverse.components.ui.skeleton";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useMorphemeDetailsTileSliceQueryOptions } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { MnemonicMarkdownRenderer } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.description";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { Platform, View } from "@-/com.npmjs.react-native";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";

/** Same system italic as the Learn card's heading mnemonic. */
const MNEMONIC_TERMS_CLASS_NAME = Platform.select({
  android: "font-[sans-serif] font-normal italic",
  ios: "font-[System] font-normal italic",
  web: "font-normal italic",
});

function exampleGloss(card: MorphemeDetailsTileSlice | null): string {
  return card?.back?.trim() || card?.pinyinList[0]?.gloss?.trim() || "No gloss available yet.";
}

/** Matches the loaded example's glyph, heading and authored mnemonic. */
export function MorphemeMnemonicsMeaningCharacterExampleSkeleton() {
  return (
    <Surface testID="mnemonic-compound-example-skeleton">
      <SurfaceHeader className="flex-row items-center gap-3 py-2.5">
        <Skeleton className="size-14 shrink-0" />
        <View className="min-w-0 flex-1 gap-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </View>
      </SurfaceHeader>
      <SurfaceContent className="gap-2 p-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </SurfaceContent>
    </Surface>
  );
}

function CharacterExampleSurface({
  card,
  character,
  onPressMnemonicTerms,
}: {
  card: MorphemeDetailsTileSlice | null;
  character: string;
  onPressMnemonicTerms: (character: string, mnemonicTerms: readonly string[]) => void;
}) {
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const mnemonic = card?.description?.trim() ?? "";
  const gloss = exampleGloss(card);
  const mnemonicTerms = card?.morphemeType === "grapheme" ? (card.mnemonicTerms ?? []) : [];

  return (
    <Surface className="rounded-xs" testID="mnemonic-compound-example">
      <SurfaceHeader className="flex-row items-center gap-3 py-2.5">
        <View
          accessibilityLabel={`${character}. ${gloss}`}
          accessible
          className="size-14 shrink-0 items-center justify-center rounded-xs border border-border bg-background"
        >
          <GraphemeFontGlyph
            className="text-[36px] leading-11 text-foreground"
            glyph={getGraphemeGlyph(character, { uncolored: true })}
          />
        </View>
        <View className="min-w-0 flex-1 gap-0.5">
          <Text className="text-[17px] leading-6 font-bold text-foreground">{gloss}</Text>
          {mnemonicTerms.length > 0 ? (
            <Text
              accessibilityHint={`Shows the mnemonics that use ${character} as a component`}
              accessibilityRole="link"
              className={`text-[15px] leading-5 text-foreground underline ${MNEMONIC_TERMS_CLASS_NAME}`}
              onPress={() => {
                onPressMnemonicTerms(character, mnemonicTerms);
              }}
              suppressHighlighting
              testID="mnemonic-compound-example-terms"
            >
              {mnemonicTerms.join(", ")}
            </Text>
          ) : null}
        </View>
      </SurfaceHeader>
      <SurfaceContent className="px-3 py-1.5">
        {mnemonic ? (
          <MnemonicMarkdownRenderer grapheme={character} mnemonic={mnemonic} />
        ) : (
          <Text className="py-1.5 text-[14px] leading-6 text-muted-foreground italic">
            No mnemonic available yet.
          </Text>
        )}
      </SurfaceContent>
    </Surface>
  );
}

/**
 * One example character in the examples sheet, as a surface: its glyph, gloss and component
 * mnemonic in the header, its authored mnemonic as the body. Pressing the mnemonic reports it so
 * the sheet can switch to the mnemonics that use that character.
 */
export function MorphemeMnemonicsMeaningCharacterExampleSurface({
  character,
  onPressMnemonicTerms,
}: {
  character: string;
  onPressMnemonicTerms: (character: string, mnemonicTerms: readonly string[]) => void;
}) {
  const { data = null, isPending } = useQuery(
    useMorphemeDetailsTileSliceQueryOptions({ morpheme: character }),
  );
  if (isPending) {
    return <MorphemeMnemonicsMeaningCharacterExampleSkeleton />;
  }
  return (
    <CharacterExampleSurface
      card={data ?? null}
      character={character}
      onPressMnemonicTerms={onPressMnemonicTerms}
    />
  );
}
